import React, { useMemo, useCallback } from 'react';

import {
  HStack,
  Text,
  ChevronRightIcon,
  type HStackProps,
} from '@frontend/components';

import { ICON_STYLES } from '../consts';
import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import { CascaderOption, CascaderCommonProps } from '../types';

interface InternalSubmenuOptionProps<OptionType extends CascaderOption> {
  optionRef?: React.Ref<HTMLButtonElement>;
  option: OptionType;
  isHighlighted?: boolean;
  onClick: (option: OptionType) => void;
}

export interface SubmenuOptionProps<OptionType extends CascaderOption>
  extends HStackProps {
  optionRef?: React.Ref<HTMLButtonElement>;
  option: OptionType;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalSubmenuOption = <OptionType extends CascaderOption>({
  option,
  onClick: internalOnClick,
  isHighlighted,
  optionRef,
}: InternalSubmenuOptionProps<OptionType>) => {
  const { cascaderStyles, getCommonProps } = useCascaderState<OptionType>();
  const { setHoverOption } = getCommonProps();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomSubmenuOption = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.SUBMENU_OPTION),
    [getComponent],
  );

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      internalOnClick(option);
    },
    [internalOnClick, option],
  );

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  const onMouseEnter = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setHoverOption(option);
    },
    [setHoverOption, option],
  );
  const onMouseLeave = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setHoverOption(null);
    },
    [setHoverOption],
  );

  return (
    <CustomSubmenuOption
      option={option}
      optionRef={optionRef}
      onClick={onClick}
      onMouseDown={onMouseDown}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      getCommonProps={getCommonProps}
      role="menuitem"
      tabIndex={-1}
      aria-selected={isHighlighted}
      data-highlighted={isHighlighted || undefined}
      __css={cascaderStyles.submenuOption}
      className="cascader-submenu-option"
    />
  );
};

export const SubmenuOption = <OptionType extends CascaderOption>({
  option,
  optionRef,
  getCommonProps: _,
  ...rest
}: SubmenuOptionProps<OptionType>) => {
  return (
    <HStack as="button" ref={optionRef} {...rest}>
      <Text size="md" as="span" flexGrow={1} textAlign="left">
        {option.label}
      </Text>
      <ChevronRightIcon {...ICON_STYLES} />
    </HStack>
  );
};
